/**
 * Sentry Error Tracking for Admin Panel
 *
 * Initializes Sentry for the admin frontend with:
 * - Error tracking
 * - Performance monitoring (browser tracing)
 * - Session replay (with strict PII masking for admin data)
 * - GDPR-compliant data filtering
 *
 * Environment Variables Required:
 * - VITE_SENTRY_DSN: Admin Sentry project DSN
 * - VITE_ENVIRONMENT: Environment name (default: development)
 * - VITE_VERSION: App version (default: 1.0.0)
 *
 * Admin Sentry DSN: https://de25b39fda5d9075c529b0d0af6e1df5@o4510385962483712.ingest.us.sentry.io/4510594042036224
 */

import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for admin panel
 *
 * This function should be called once at app startup (in main.jsx)
 * before React renders.
 *
 * @returns {void}
 */
export const initSentry = ({ gpc = false } = {}) => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Skip initialization if DSN not configured
  if (!dsn) {
    console.warn('⚠️ VITE_SENTRY_DSN not configured. Sentry will be disabled.');
    return;
  }

  try {
    // GPC: disable replay and tracing when Global Privacy Control is active
    const integrations = [
      Sentry.browserTracingIntegration(),
    ];
    if (!gpc) {
      integrations.push(
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
          maskAllInputs: true,
        }),
      );
    }

    Sentry.init({
      dsn: dsn,

      integrations,

      // Performance Monitoring — disabled under GPC
      tracesSampleRate: gpc ? 0 : parseFloat(
        import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || "0.2"
      ),

      // Session Replay — disabled under GPC
      replaysSessionSampleRate: gpc ? 0 : parseFloat(
        import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || "0.3"
      ),

      replaysOnErrorSampleRate: gpc ? 0 : parseFloat(
        import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || "1.0"
      ),

      // Environment & Release
      environment: import.meta.env.VITE_ENVIRONMENT || "development",
      release: `impleo-admin@${import.meta.env.VITE_VERSION || "1.0.0"}`,

      // Additional Options
      attachStacktrace: true,
      normalizeDepth: 10,

      // GDPR Compliance: Strict filtering for admin panel
      beforeSend(event, hint) {
        // Remove ALL headers (admin tokens are highly sensitive)
        if (event.request?.headers) {
          delete event.request.headers;
        }

        // Remove all request data (may contain user PII)
        if (event.request?.data) {
          event.request.data = '[REDACTED - Admin Data]';
        }

        // Remove query strings (may contain sensitive filters)
        if (event.request?.query_string) {
          event.request.query_string = '[REDACTED]';
        }

        // Remove localStorage/sessionStorage data
        if (event.contexts?.['Local Storage']) {
          delete event.contexts['Local Storage'];
        }
        if (event.contexts?.['Session Storage']) {
          delete event.contexts['Session Storage'];
        }

        // Tag as admin for filtering in Sentry dashboard
        event.tags = event.tags || {};
        event.tags.app_type = 'admin';

        return event;
      },

      // Ignore common non-error events
      ignoreErrors: [
        'top.GLOBALS',
        'chrome-extension://',
        'moz-extension://',
        'NetworkError',
        'Network request failed',
        'ResizeObserver loop limit exceeded',
        'Non-Error promise rejection captured',
      ],

      // Don't report errors from these URLs
      denyUrls: [
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
      ],
    });

    console.log('✅ Sentry initialized for Admin Panel');
    console.log(`📊 Environment: ${import.meta.env.VITE_ENVIRONMENT || "development"}`);

  } catch (error) {
    console.error('❌ Failed to initialize Sentry:', error);
  }
};

/**
 * Capture exception to Sentry
 */
export const captureSentryException = (error, context = {}) => {
  Sentry.captureException(error, { extra: context });
};

/**
 * Capture message to Sentry
 */
export const captureSentryMessage = (message, level = 'info') => {
  Sentry.captureMessage(message, level);
};

/**
 * Set admin user context
 *
 * @param {Object} admin - Admin user object
 * @param {string} admin.id - Admin ID (use backup-admin for backup auth)
 * @param {string} admin.email - Admin email
 * @param {string} admin.role - Admin role (super_admin, admin, etc.)
 */
export const setSentryUser = (admin) => {
  if (!admin) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: admin.id || admin.sub,
    email: admin.email,
    // Include role for debugging (not PII)
    role: admin.role,
  });
};

/**
 * Clear admin user context (call on logout)
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

/**
 * Add breadcrumb for debugging
 */
export const addSentryBreadcrumb = (message, category = 'default', level = 'info', data = {}) => {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  });
};

export default {
  initSentry,
  captureSentryException,
  captureSentryMessage,
  setSentryUser,
  clearSentryUser,
  addSentryBreadcrumb,
};
